import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { Configuration, GlobalAudioLibraryApi, AudioFileResponseDto } from "../api/client-axios";
import { ApiError } from "../model/apiError";
import { Problem } from "../model/problem";
import { readFileAsync } from "../utils/fileUtils";
import { apiConfiguration } from "./server";
import { applyRejection } from "./thunkError";


export type AudioResult ={
    audio: AudioFileResponseDto, url: string
}

export interface AudiosLibraryState {
    audios?: AudioResult[],
    selectedAudio?: AudioFileResponseDto,
    status: "idle" | "loading" | "error" | "success" | "auth-error" | "deleting";
    error?: string | Problem;
    code?: number;
    loaded: number;
    total: number;
}


const initialAudiosLibraryState: AudiosLibraryState = {
    status: "idle",
    error: "",
    loaded: 0,
    total: 0
}


// Fetches only the audio metadata list, then kicks off an independent content
// fetch per audio so they can stream in one-by-one instead of blocking on the
// whole batch. Returns the list so the reducer can set the expected total.
export const fetchAudios = createAsyncThunk<AudioFileResponseDto[],void, { rejectValue: ApiError }>('audioLibrary/fetchAudios', async (_, thunkApi) => {
    try {
        const api = new GlobalAudioLibraryApi(apiConfiguration());
        const response = await api.apiV1AdministrationAudioLibraryGet();
        const audios = response.data as AudioFileResponseDto[];
        audios.forEach((audio) => {
            thunkApi.dispatch(fetchAudioContent(audio));
        });
        return audios;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});

// Fetches the binary content for a single audio and resolves it to a data URL.
// Each resolution appends to the store, so audios appear progressively.
export const fetchAudioContent = createAsyncThunk<AudioResult, AudioFileResponseDto, { rejectValue: ApiError }>('audioLibrary/fetchAudioContent', async (audio, thunkApi) => {
    try {
        const api = new GlobalAudioLibraryApi(apiConfiguration());
        const r = await api.apiV1AdministrationAudioLibraryIdContentGet(audio.id!, { responseType: 'blob' });
        const url = await readFileAsync(r.data);
        return { audio, url: url as string } as AudioResult;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});


// export const uploadAudio = createAsyncThunk<AudioFileResponseDto[], void, { rejectValue: ApiError }>('audioLibrary/uploadAudio', async (args, thunkApi) => {
//     try {
//         const api = new GlobalAudioLibraryApi(apiConfiguration);
//         await api.apiV1AdministrationAudioLibraryPost(args);
//         const response = await api.apiV1AdministrationAudioLibraryGet();
//         return response.data;
//     } catch (error: AxiosError | any) {
//         return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
//     }
// });

// export const deleteAudio = createAsyncThunk<AudioFileResponseDto[],string, { rejectValue: ApiError }>('audioLibrary/deleteAudio', async (audioId, thunkApi) => {
//     try {
//         const api = new GlobalAudioLibraryApi(apiConfiguration);
//         await api.apiV1AdministrationAudioLibraryIdDelete(audioId);
//         const response = await api.apiV1AdministrationAudioLibraryGet();
//         return response.data;
//     } catch (error: AxiosError | any) {
//         return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
//     }
// });


export const selectAudio = createAction("audioLibrary/selectAudio", (audio: AudioFileResponseDto) => ({ payload: audio }));
export const clearErrorAudios = createAction("audioLibrary/clearErrorAudios");
export const startDeleteAudio = createAction("audioLibrary/startDeleteAudio");
export const showAudioError = createAction("audioLibrary/showAudioError", (error: Problem) => ({ payload: error }));



const audiosLibrarysSlice = createSlice({
    name: 'audioLibrary',
    initialState: initialAudiosLibraryState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchAudios.pending, (state, _) => {
                state.status = "loading";
                state.audios = [];
                state.loaded = 0;
                state.total = 0;
            })
            .addCase(fetchAudios.fulfilled, (state, action) => {
                state.total = action.payload.length;
                // Show the page immediately; contents stream in via fetchAudioContent.
                state.status = "success";
            })
            .addCase(fetchAudios.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(fetchAudioContent.fulfilled, (state, action) => {
                state.audios = [...(state.audios ?? []), action.payload];
                state.loaded += 1;
            })
            .addCase(fetchAudioContent.rejected, (state) => {
                // Count failures toward progress so the indicator can complete.
                state.loaded += 1;
            })
            // .addCase(deleteAudio.pending, (state, _) => {
            //     state.status = "loading";
            // })
            // .addCase(deleteAudio.fulfilled, (state, action) => {
              
            //     state.status = "success";
            // })
            // .addCase(deleteAudio.rejected, (state, action) => {
            //     if (action.payload) {
            //         state.status = "auth-error";
            //         state.error = action.payload.message;
            //         state.code = action.payload.statusCode;
            //     } else {
            //         state.status = "error";
            //         state.error = action.error.message;
            //     }
            // })
            .addCase(selectAudio, (state, action) => {
                state.selectedAudio = action.payload;
            })
            .addCase(clearErrorAudios, (state) => {
                state.status = "success";
             })
             .addCase(startDeleteAudio, (state) => {
                 state.status = "deleting";
             }) 
             .addCase(showAudioError,(state, action) => {
                state.status = "error";
                state.error = action.payload;
             }) 
            ;


    }
});

export default audiosLibrarysSlice.reducer;
